// React Imports
import React, { useEffect, useState } from "react";

// Router Imports
import { useNavigate } from "react-router-dom";

// Redux Imports
import { useDispatch, useSelector } from "react-redux";
import { setHideBreadCrumb } from "../../../redux/features/hideBreadCrumb/hideBreadCrumb";

// Utils Imports
import { GlobalConfiguration } from "../../../utils/configurationProvider";

// Component Imports
import ICONS from "../../../assets/Icons";
import StatusContainer from "../../_utils/CommonStatus/StatusContainer";
import TabsWeapperSection from "../../_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import InfoCard from "../../_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import FormatAmount from "../../_utils/FormatAmount/FormatAmount";

const FieldForceRequestMultiApprovalError = () => {
  // 1️⃣ useDispatch & useNavigate
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 2️⃣ useState
  const [ErrorPageData, setErrorPageData] = useState([]);

  // 3️⃣ useSelector
  const { FieldForceRequestMultiTxnWaitingApprovalError } = useSelector(
    (state) => state.fieldForceTransaction,
  );

  // 4️⃣ Constants / Derived values
  const actionType = FieldForceRequestMultiTxnWaitingApprovalError
    ? "approve"
    : "reject";

  const findCommonNames = (arr1, arr2) => {
    const type = arr2.some((item) => typeof item === "string");
    if (type) return arr2;
    const arr2Map = new Map(arr2?.map((item) => [item?.id, item?.errors]));
    const matchId = (item) => item?.request_id;
    const matched = arr1.filter((item) => arr2Map?.has(matchId(item)));
    if (matched.length > GlobalConfiguration.lengthCheckZero) {
      return matched.map((item) => ({
        ...item,
        errors: arr2Map?.get(matchId(item)),
      }));
    }
    return (
      arr2?.map((item) => ({
        id: item?.id,
        request_id: item?.id,
        errors: item?.errors,
        field_force_account_name: "—",
        requested_amount: "—",
      })) ?? []
    );
  };

  const getFieldForceErrors = () => {
    const rawErrors = JSON.parse(
      sessionStorage.getItem("fieldForceTxnRequestError"),
    );
    if (!rawErrors) return [];

    // Transform API errors like { base: ["Error message"] } into structure findCommonNames expects
    if (typeof rawErrors === "object" && !Array.isArray(rawErrors)) {
      return Object.entries(rawErrors).flatMap(([key, value]) => {
        const errorList = Array.isArray(value) ? value : [value];
        return errorList.map((err) => ({
          id: key,
          errors: err,
        }));
      });
    }
    return Array.isArray(rawErrors) ? rawErrors : [rawErrors];
  };

  const filterErrorTxn = findCommonNames(ErrorPageData, getFieldForceErrors());

  // 5️⃣ Callback / Handler functions
  const buttonRows = [
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        handleFunction: () => {
          navigate("/field_force_transaction_dashboard");
        },
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "dark",
      },
    ],
  ];

  const getTitle = () =>
    actionType === "reject"
      ? "Oops! Request rejection failed."
      : "Oops! Request approval failed.";

  const getSubtitle = () => {
    const action = actionType === "reject" ? "rejection" : "approval";
    return ` An error occurred with the following request, causing the ${action} of all the requests to fail.`;
  };

  // 6️⃣ useEffect
  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));
    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const storedSelectedApproval = JSON.parse(
      sessionStorage.getItem("selectedReimbursementRequestTxnData"),
    );
    if (storedSelectedApproval) {
      setErrorPageData(storedSelectedApproval?.selectedSendTransactions);
    } else {
      setErrorPageData([]);
    }
  }, []);

  return (
    <div>
      <StatusContainer
        status="error"
        buttonRows={buttonRows}
        title={getTitle()}
        subtitle={getSubtitle()}
      >
        <TabsWeapperSection title="Error details">
          <>
            {Array.isArray(filterErrorTxn) &&
              filterErrorTxn.map((val, i) => {
                if (typeof val === "string") {
                  return (
                    <InfoCard
                      bgColor="var(--negative-card)"
                      key={i}
                      rows={[
                        {
                          leftTitle: val,
                          leftSubTitle: "Error message",
                        },
                      ]}
                    />
                  );
                }
                const errorList = Array.isArray(val.errors)
                  ? val.errors
                  : val.errors != null
                    ? [val.errors]
                    : [];
                return (
                  <React.Fragment key={val.request_id ?? i}>
                    {errorList.map((error, index) => (
                      <InfoCard
                        bgColor="var(--negative-card)"
                        key={`${val.request_id ?? i}-${index}`}
                        rows={[
                          {
                            leftTitle:
                              typeof error === "object"
                                ? (error?.reason ??
                                  error?.message ??
                                  error?.errors[0]?.reason[0] ??
                                  "")
                                : String(error),
                            leftSubTitle: "Error message",
                          },
                        ]}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
          </>
        </TabsWeapperSection>
        <TabsWeapperSection title="Request details">
          <>
            {Array.isArray(ErrorPageData) &&
              ErrorPageData.map((val, i) => {
                if (typeof val === "string") return null;
                return (
                  <InfoCard
                    key={val.request_id || i}
                    bgColor="var(--negative-card)"
                    hoverable="white"
                    onClick={() =>
                      navigate(
                        `/field_force_transaction_dashboard/field_force_request_view_all/field_force_request_detail_page/${val?.request_id}`,
                      )
                    }
                    rows={[
                      {
                        leftTitle: val?.field_force_account_name ?? "—",
                        leftSubTitle: "Account name",
                        hasRight: true,
                        rightText: true,
                        rightTitle:
                          val?.requested_amount != null &&
                          val?.requested_amount !== "—" ? (
                            <>
                              INR <FormatAmount price={val?.requested_amount} />
                            </>
                          ) : (
                            "—"
                          ),
                        rightTitleTextColor: "var(--text-primary)",
                        rightTextFontWeight: "semibold",
                        rightSubTitle: "Amount",
                        rightIcon: true,
                        onClick: () =>
                          navigate(
                            `/field_force_transaction_dashboard/field_force_request_view_all/field_force_request_detail_page/${val?.request_id}`,
                          ),
                      },
                    ]}
                  />
                );
              })}
          </>
        </TabsWeapperSection>
      </StatusContainer>
    </div>
  );
};
export default FieldForceRequestMultiApprovalError;
